import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import BRAND from "../theme/talktokam";

const CookiePolicy = () => {
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          overflow: "hidden",
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              🍪 Cookie Policy
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", color: "#94a3b8" }}>
              Last updated: March 2025
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container sx={{ maxWidth: 800 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                "& h3": {
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  color: "#fff",
                  mt: 4,
                  mb: 1.5,
                },
                "& p": {
                  fontSize: "0.95rem",
                  color: "#94a3b8",
                  lineHeight: 1.8,
                  mb: 2,
                },
                "& ul": { pl: 2.5, mb: 2 },
                "& li": { fontSize: "0.9rem", color: "#94a3b8", mb: 0.5 },
              }}
            >
              <h3>1. What Are Cookies</h3>
              <p>
                Cookies are small text files stored on your device when you
                visit a website. They help websites remember your preferences,
                authenticate your session, and analyze usage patterns.
              </p>

              <h3>2. How We Use Cookies</h3>
              <p>
                Talktokam uses cookies and similar technologies for the
                following purposes:
              </p>
              <ul>
                <li>
                  <strong>Essential Cookies:</strong> Required for the platform
                  to function. They enable user authentication, session
                  management, and security features.
                </li>
                <li>
                  <strong>Analytics Cookies:</strong> Help us understand how
                  users interact with our website and platform, so we can
                  improve the experience.
                </li>
                <li>
                  <strong>Preference Cookies:</strong> Remember your settings
                  and preferences, such as language and theme.
                </li>
                <li>
                  <strong>Marketing Cookies:</strong> Used to deliver relevant
                  advertisements and measure their effectiveness (only with your
                  consent).
                </li>
              </ul>

              <h3>3. Third-Party Cookies</h3>
              <p>We may use third-party services that set their own cookies:</p>
              <ul>
                <li>Google Analytics — for website usage analysis</li>
                <li>Stripe — for payment processing (essential)</li>
                <li>Intercom or similar — for customer support chat</li>
                <li>Social media platforms — for sharing functionality</li>
              </ul>

              <h3>4. Managing Cookies</h3>
              <p>
                You can control cookies through your browser settings. Most
                browsers allow you to block or delete cookies. However,
                disabling essential cookies may affect the functionality of our
                platform.
              </p>
              <p>To manage cookie preferences in your browser:</p>
              <ul>
                <li>
                  Chrome: Settings → Privacy and Security → Cookies and other
                  site data
                </li>
                <li>
                  Firefox: Options → Privacy & Security → Cookies and Site Data
                </li>
                <li>
                  Safari: Preferences → Privacy → Cookies and website data
                </li>
                <li>
                  Edge: Settings → Site permissions → Cookies and site data
                </li>
              </ul>

              <h3>5. Consent</h3>
              <p>
                When you first visit our website, we display a cookie consent
                banner. Essential cookies are set without consent as they are
                necessary for the service to function. Non-essential cookies are
                set only after you provide consent.
              </p>
              <p>
                You can change your consent preferences at any time by clicking
                the cookie settings link in the footer.
              </p>

              <h3>6. Updates</h3>
              <p>
                We may update this Cookie Policy from time to time. Changes will
                be posted on this page with an updated date.
              </p>

              <h3>7. Contact</h3>
              <p>
                If you have questions about our use of cookies, contact us at
                support@talktokam.com.
              </p>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default CookiePolicy;
